/* Given a string, return either a valid IRI or a text string
 * that will serve as the value for an SVG reference.
 *
 * We want to avoid making a FuncIRI out of any of the names in STRING_REFERENCE_VALUES or a color.
 *
 * See these references:
 *   https://www.w3.org/TR/SVG/linking.html#IRIforms
 *   https://www.w3.org/TR/SVGTiny12/linking.html#IRIReference
 *
 * Examples:
 *   stroke="none"
 *   fill="#fff"
 *   fill="#ffffff"
 *   fill="rgb(255,255,255)"
 *   fill="rgb(100%,100%,100%)"
 *   use="url(#MyShape)"
 *   style="fill:url(#MyGradient)"
 */
// TODO what about data URIs (maybe applicable to SVG 1.2 Tiny, not SVG 1.1)?
// TODO what about values for the src attribute for color profiles,
// especially ones like "local(" + <string> + ")"?
// These are the allowed values:
// sRGB | <local-profile> | <iri> | (<local-profile> <iri>) | inherit
// https://www.w3.org/TR/SVG/color.html#ColorProfileSrcProperty
import { find, map, reduce } from "lodash/fp";
const urlRegex = require("url-regex");
import { Validator } from "collit";
export const STRING_REFERENCE_VALUES = [
    "none",
    "inherit",
    "currentColor"
];
function getFuncIRI(iri) {
    return `url(${iri})`;
}
const formatters = [
    {
        type: "string",
        test: (input) => STRING_REFERENCE_VALUES.indexOf(input) > -1,
        format: (input) => input
    },
    {
        type: "nonLocalIRI",
        // relative URL
        test: (input) => urlRegex({ strict: true, exact: true }).test(input) ||
            input.match(/^(\/|\.\/|\.\.\/)/),
        format: getFuncIRI
    },
    {
        type: "color",
        test: (input) => Validator.isColor(input),
        format: (input) => input
    },
    {
        type: "localIRI",
        test: (input) => true,
        format: (input) => getFuncIRI("#" + input)
    }
];
const formatterTypes = map(({ type }) => type, formatters);
const formatterMap = reduce(function (acc, { type, test, format }) {
    acc[type] = { type, test, format };
    return acc;
}, {}, formatters);
function getSVGReferenceFormatter(input, potentialFormatterTypes = formatterTypes) {
    const potentialFormatterTypeCount = potentialFormatterTypes.length;
    if (potentialFormatterTypeCount === 1) {
        const formatterType = potentialFormatterTypes[0];
        return formatterMap[formatterType];
    }
    else {
        return find(function ({ type, test }) {
            return potentialFormatterTypes.indexOf(type) > -1 && test(input);
        }, formatters);
    }
}
export function getSVGReferenceType(input, potentialFormatterTypes = formatterTypes) {
    return getSVGReferenceFormatter(input, potentialFormatterTypes).type;
}
export function formatSVGReference(input, potentialFormatterTypes = formatterTypes) {
    return getSVGReferenceFormatter(input, potentialFormatterTypes).format(input);
}
//# sourceMappingURL=data:application/json;base64,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